import React from 'react';

import { BRAND_ASSETS } from '../brand/assets';
import { WisemanId, WisemanPersona, WISEMEN } from '../wisemen/personas';

export type CouncilPhase = 'idle' | 'consulting' | 'answered';

interface ThreeWisemenCouncilProps {
  phase: CouncilPhase;
  leadId?: WisemanId;
  selectedId?: WisemanId;
  onSelectAgent?: (id: WisemanId) => void;
}

function phaseLabel(phase: CouncilPhase, lead?: WisemanPersona, selected?: WisemanPersona): string {
  if (phase === 'consulting' && lead) {
    return `${lead.name} is paging through the library…`;
  }
  if (phase === 'answered' && lead) {
    return `${lead.name} answered last`;
  }
  if (selected) {
    return `${selected.name} is selected — ask anything in their lane or override anytime`;
  }
  return 'Three agents, three voices — pick one or let the council route your question';
}

const ThreeWisemenCouncil: React.FC<ThreeWisemenCouncilProps> = ({
  phase,
  leadId,
  selectedId,
  onSelectAgent,
}) => {
  const lead = leadId ? WISEMEN.find((w) => w.id === leadId) : undefined;
  const selected = selectedId ? WISEMEN.find((w) => w.id === selectedId) : undefined;

  return (
    <section className="wisemen-council" aria-label="The Three Wisemen">
      <div className="wisemen-council__banner">
        <img
          src={BRAND_ASSETS.councilIllustration}
          alt="Sasha, Pickle Rick, and Dr. Gonzo consulting the research library"
          className="wisemen-council__illustration"
        />
      </div>
      <img
        src={BRAND_ASSETS.logoCosmic}
        alt=""
        aria-hidden
        className="wisemen-council__seal"
      />

      <p className="wisemen-council__status">{phaseLabel(phase, lead, selected)}</p>

      <ul className="wisemen-council__roster">
        {WISEMEN.map((wiseman) => {
          const isLead = wiseman.id === leadId;
          const isSelected = wiseman.id === selectedId;
          const isActive =
            isSelected ||
            phase === 'consulting' ||
            (phase === 'answered' && isLead);
          const Tag = onSelectAgent ? 'button' : 'div';
          return (
            <li key={wiseman.id}>
              <Tag
                type={onSelectAgent ? 'button' : undefined}
                className={[
                  'wisemen-card',
                  isActive ? 'wisemen-card--active' : '',
                  isLead ? 'wisemen-card--lead' : '',
                  isSelected ? 'wisemen-card--selected' : '',
                  onSelectAgent ? 'wisemen-card--selectable' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={{ '--wiseman-accent': wiseman.accent } as React.CSSProperties}
                onClick={onSelectAgent ? () => onSelectAgent(wiseman.id) : undefined}
                aria-pressed={onSelectAgent ? isSelected : undefined}
              >
                <div className="wisemen-card__avatar" aria-hidden>
                  {wiseman.name.charAt(0)}
                </div>
                <div className="wisemen-card__body">
                  <p className="wisemen-card__name">{wiseman.name}</p>
                  <p className="wisemen-card__epithet">{wiseman.epithet}</p>
                  <p className="wisemen-card__focus">{wiseman.focus}</p>
                </div>
                {phase === 'consulting' && isLead && (
                  <span className="wisemen-card__pulse" aria-hidden />
                )}
              </Tag>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default ThreeWisemenCouncil;
